import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, CheckCheck, Plus, Trash2, GripVertical, ArrowUp, ArrowDown, Check, Minus, X, Lock, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AttendanceRegisterEditor } from "@/components/AttendanceQuickPickerDialog";
import DuplicateTrainingDialog from "@/components/DuplicateTrainingDialog";
import TrainingSimulationCard from "@/components/TrainingSimulationCard";
import { Copy } from "lucide-react";

export const CATEGORIES = ["Mobilità", "Resistenza", "Forza", "Potenza", "Velocità", "Rapidità", "Footwork", "Tecnica", "Tattica", "Assalti", "Lezione individuale", "Test", "Recupero", "Altro"];
const KIND_TO_CATEGORY = { riscaldamento: "Mobilità", mobilita: "Mobilità", "mobilità": "Mobilità", preparazione: "Forza", prep_fisica: "Forza", circuito: "Resistenza", footwork: "Footwork", "gambe-scherma": "Footwork", gambe_scherma: "Footwork", tecnica: "Tecnica", coppia: "Tecnica", coppie: "Tecnica", tattica: "Tattica", assalti: "Assalti", assalti_tema: "Assalti", assalti_liberi: "Assalti", simulazione: "Assalti", lezione: "Lezione individuale", test: "Test", defaticamento: "Recupero", recupero: "Recupero" };
const blockCategory = (b) => (CATEGORIES.includes(b.category) ? b.category : (KIND_TO_CATEGORY[(b.kind || "").toLowerCase()] || "Altro"));


const BLOCK_KINDS = [
  { k: "riscaldamento", l: "Riscaldamento" },
  { k: "mobilita", l: "Mobilità" },
  { k: "preparazione", l: "Preparazione fisica" },
  { k: "footwork", l: "Gambe-scherma" },
  { k: "tecnica", l: "Tecnica" },
  { k: "tattica", l: "Tattica" },
  { k: "coppia", l: "Esercizi a coppia" },
  { k: "lezione", l: "Lezione individuale" },
  { k: "assalti", l: "Assalti" },
  { k: "defaticamento", l: "Defaticamento" },
];

export default function TrainingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [registerId, setRegisterId] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [diary, setDiary] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deps, setDeps] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES);
  const [simGroups, setSimGroups] = useState([]);

  useEffect(() => {
    api.get("/users").then((r) => setCoaches((r.data || []).filter((u) => ["coach", "admin", "trainer"].includes(u.role)))).catch(() => {});
    api.get("/meta/taxonomy").then((r) => setCategories(r.data.categories || CATEGORIES)).catch(() => {});
    api.get("/groups").then((r) => setSimGroups(r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    const t = await api.get(`/trainings/${id}`);
    setTraining({ ...t.data, blocks: t.data.blocks || [] });
    const ex = await api.get(`/exercises`);
    setExercises(ex.data.filter((x) => !x.archived));
    api.get(`/training/session/${id}`).then((r) => setDiary(r.data)).catch(() => setDiary(null));
  };
  useEffect(() => { load(); }, [id]);
  const openDelete = async () => {
    try { const { data } = await api.get(`/trainings/${id}/dependencies`); setDeps(data); setDeleteOpen(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const confirmDelete = async () => {
    try { await api.delete(`/trainings/${id}/safe`); toast.success("Seduta eliminata"); navigate("/allenamento"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };
  const cancelTraining = async () => {
    try { await api.post(`/trainings/${id}/cancel`); toast.success("Seduta annullata: i dati collegati restano nello storico"); setDeleteOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const linkLesson = async (lid) => {
    try { await api.post(`/lessons/${lid}/link-training`, { training_id: id }); toast.success("Lezione collegata a questa seduta"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore collegamento"); }
  };


  // v2.1.1 · Presenze = AttendanceRegister canonico della seduta (find-or-create, data/gruppo dal training)
  const openRegister = async () => {
    try {
      const { data } = await api.post("/attendance", { training_id: id });
      setRegisterId(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore apertura registro"); }
  };

  const addBlock = (kind) => {
    const k = BLOCK_KINDS.find((x) => x.k === kind);
    setTraining((t) => ({ ...t, blocks: [...(t.blocks || []), { kind, title: k.l, duration: 10, notes: "", exercise_ids: [] }] }));
  };
  const updateBlock = (i, patch) => setTraining((t) => { const bl = [...t.blocks]; bl[i] = { ...bl[i], ...patch }; return { ...t, blocks: bl }; });
  const removeBlock = (i) => setTraining((t) => ({ ...t, blocks: t.blocks.filter((_, idx) => idx !== i) }));
  const moveBlock = (i, dir) => setTraining((t) => {
    const bl = [...t.blocks]; const ni = i + dir;
    if (ni < 0 || ni >= bl.length) return t;
    [bl[i], bl[ni]] = [bl[ni], bl[i]]; return { ...t, blocks: bl };
  });

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    setTraining((t) => {
      const bl = [...t.blocks];
      const [m] = bl.splice(dragIdx, 1);
      bl.splice(i, 0, m);
      return { ...t, blocks: bl };
    });
    setDragIdx(null);
  };

  const toggleExercise = (i, exId) => {
    setTraining((t) => {
      const bl = [...t.blocks];
      const ids = bl[i].exercise_ids || [];
      bl[i] = { ...bl[i], exercise_ids: ids.includes(exId) ? ids.filter((x) => x !== exId) : [...ids, exId] };
      return { ...t, blocks: bl };
    });
  };

  const saveTraining = async () => {
    await api.put(`/trainings/${id}`, training);
    toast.success("Allenamento salvato");
  };

  if (!training) return <div className="text-zinc-500">Caricamento…</div>;
  const totalDuration = (training.blocks || []).reduce((s, b) => s + (parseInt(b.duration) || 0), 0);

  return (
    <div className="space-y-6">
      <Link to="/allenamento" className="text-sm text-zinc-500 flex items-center gap-1 hover:text-black"><ArrowLeft className="w-4 h-4" />Torna agli allenamenti</Link>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">{training.title}</h1>
          <div className="text-sm text-zinc-500 font-mono mt-1">{training.date} · {training.duration}′ · Intensità {training.intensity}</div>
          {training.objective && <p className="mt-2 text-zinc-700">{training.objective}</p>}
        </div>
        <div className="flex items-center gap-2">
          {training.status === "completed" && <span data-testid="training-completed-badge" className="text-xs uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1">Completata</span>}
          <DuplicateTrainingDialog training={training} onDone={(d) => navigate(`/allenamento/${d.id}`)} trigger={<Button data-testid="duplicate-training-btn" variant="outline" className="h-12"><Copy className="w-4 h-4 mr-2" />Duplica</Button>} />
          <Button data-testid="delete-training-btn" variant="outline" className="h-12 text-red-700 border-red-200 hover:bg-red-50" onClick={openDelete}><Trash2 className="w-4 h-4 mr-2" />Elimina</Button>
          {training.status === "annullata" && <span data-testid="training-cancelled-badge" className="text-xs uppercase tracking-wider bg-zinc-100 text-zinc-600 border border-zinc-300 rounded-full px-3 py-1">Annullata</span>}
          <Button data-testid="close-session-btn" onClick={() => setCloseOpen(true)} className="h-12 bg-[#0052FF] hover:bg-blue-700 text-white">
            <CheckCheck className="w-4 h-4 mr-2" />{training.status === "completed" ? "Modifica chiusura" : "Chiudi seduta"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="diary">
        <TabsList>
          <TabsTrigger value="diary" data-testid="tab-diary">Diario tecnico</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Presenze</TabsTrigger>
          <TabsTrigger value="blocks" data-testid="tab-blocks">Blocchi</TabsTrigger>
        </TabsList>

        <TabsContent value="diary" className="pt-6 space-y-6">
          <SessionDiary diary={diary} training={training} onLinkLesson={linkLesson} />
          <TrainingSimulationCard training={training} groups={simGroups} />
        </TabsContent>

        <TabsContent value="attendance" className="pt-6">
          {registerId ? (
            <AttendanceRegisterEditor registerId={registerId} onClose={() => { setRegisterId(null); load(); }} onSaved={load} />
          ) : (
            <div className="bg-white border border-zinc-200 rounded-md p-8 text-center space-y-3" data-testid="attendance-canonical-cta">
              <div className="text-sm text-zinc-600">
                {diary?.realized?.has_register
                  ? `Registro presenze della seduta: ${diary.comparison?.present_athletes ?? 0} presenti su ${diary.comparison?.expected_athletes ?? 0} attesi.`
                  : "Nessun registro ancora creato: verrà generato dal gruppo della seduta con la data della seduta."}
              </div>
              <Button data-testid="open-attendance-register" className="h-12 bg-black hover:bg-zinc-800" onClick={openRegister}><CheckCheck className="w-4 h-4 mr-2" />{diary?.realized?.has_register ? "Apri registro presenze" : "Crea registro presenze"}</Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="blocks" className="pt-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-heading font-semibold">Costruttore blocchi</div>
              <div className="text-xs text-zinc-500 font-mono">Totale pianificato: {totalDuration}′ / {training.duration}′</div>
            </div>
            <div className="flex items-center gap-2">
              <Select onValueChange={(v) => addBlock(v)}>
                <SelectTrigger data-testid="add-block-select" className="w-56 h-11"><SelectValue placeholder="+ Aggiungi blocco" /></SelectTrigger>
                <SelectContent>{BLOCK_KINDS.map((b) => <SelectItem key={b.k} value={b.k}>{b.l}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid="save-blocks" onClick={saveTraining} className="h-11 bg-black hover:bg-zinc-800">Salva</Button>
            </div>
          </div>

          <div className="space-y-3">
            {(training.blocks || []).map((b, i) => (
              <div
                key={i}
                draggable
                onDragStart={() => onDragStart(i)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(i)}
                data-testid={`block-${i}`}
                className={`bg-white border rounded-md p-4 ${dragIdx === i ? "border-black opacity-60" : "border-zinc-200"}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1 pt-1">
                    <GripVertical className="w-4 h-4 text-zinc-400 cursor-grab" />
                    <button onClick={() => moveBlock(i, -1)} className="p-1 text-zinc-400 hover:text-black"><ArrowUp className="w-3 h-3" /></button>
                    <button onClick={() => moveBlock(i, 1)} className="p-1 text-zinc-400 hover:text-black"><ArrowDown className="w-3 h-3" /></button>
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-[10px] uppercase tracking-wider bg-black text-white px-2 py-1 rounded font-mono">{i + 1}</span>
                        <span className="text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-700 px-2 py-1 rounded">{b.kind}</span>
                        <Input className="flex-1 max-w-md h-9" value={b.title} onChange={(e) => updateBlock(i, { title: e.target.value })} />
                        <div className="flex items-center gap-1">
                          <Input type="number" className="w-20 h-9 font-mono" value={b.duration || 0} onChange={(e) => updateBlock(i, { duration: parseInt(e.target.value || 0) })} />
                          <span className="text-xs text-zinc-500">min</span>
                        </div>
                      </div>
                      <button onClick={() => removeBlock(i)} className="p-2 text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <Textarea rows={2} placeholder="Note" value={b.notes || ""} onChange={(e) => updateBlock(i, { notes: e.target.value })} />
                    <div>
                      <Label className="text-xs">Tecnici assegnati ({(b.coach_ids || []).length})</Label>
                      <CoachChips coaches={coaches} selected={b.coach_ids || []} testidPrefix={`block-${i}`}
                        onToggle={(cid) => updateBlock(i, { coach_ids: (b.coach_ids || []).includes(cid) ? b.coach_ids.filter((x) => x !== cid) : [...(b.coach_ids || []), cid] })} />
                      <AssignSuggest training={training} category={blockCategory(b)} blockRef={String(i)} selected={b.coach_ids || []}
                        onToggle={(cid) => updateBlock(i, { coach_ids: (b.coach_ids || []).includes(cid) ? b.coach_ids.filter((x) => x !== cid) : [...(b.coach_ids || []), cid] })} />
                    </div>
                    <div>
                      <Label className="text-xs">Esercizi assegnati ({(b.exercise_ids || []).length})</Label>
                      <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto no-scrollbar">
                        {exercises
                          .filter((e) => b.kind === "riscaldamento" || b.kind === "preparazione" || b.kind === "mobilita" || b.kind === "defaticamento" ? e.macro_category === "atletico" : e.macro_category === "scherma")
                          .map((e) => (
                            <button
                              key={e.id}
                              onClick={() => toggleExercise(i, e.id)}
                              className={`text-xs px-2 py-1 rounded-md border ${(b.exercise_ids || []).includes(e.id) ? "bg-black text-white border-black" : "bg-white border-zinc-200 hover:bg-zinc-50"}`}
                            >{e.title}</button>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {(training.blocks || []).length === 0 && (
              <div className="text-center py-10 text-zinc-500 bg-zinc-50 border border-dashed border-zinc-300 rounded-md">
                Nessun blocco. Usa "+ Aggiungi blocco" per iniziare.
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      {closeOpen && <CloseSessionDialog training={training} diary={diary} coaches={coaches} categories={categories} onClose={() => setCloseOpen(false)} onSaved={() => { setCloseOpen(false); load(); }} />}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent data-testid="delete-training-dialog">
          <DialogHeader><DialogTitle>Elimina seduta</DialogTitle></DialogHeader>
          {deps?.deletable ? (
            <p className="text-sm text-zinc-600" data-testid="delete-training-ok">Nessun dato realizzato collegato. La seduta «{training.title}» del {training.date} verrà eliminata definitivamente.</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3" data-testid="delete-training-blocked">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>Eliminazione bloccata: la seduta contiene dati realizzati. Nessuna cancellazione a cascata.</div>
              </div>
              <ul className="text-sm divide-y divide-zinc-100 border border-zinc-200 rounded" data-testid="delete-training-deps">
                {(deps?.blocking || []).map((b) => <li key={b.key} className="flex justify-between px-3 py-2" data-testid={`dep-${b.key}`}><span>{b.label}</span><span className="font-mono font-semibold">{b.count}</span></li>)}
              </ul>
              <p className="text-xs text-zinc-500">Rimuovi prima i dati collegati (registro, lezioni, attività, simulazioni) oppure <b>annulla</b> la seduta: resta nello storico, marcata come annullata, senza perdere nulla.</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Chiudi</Button>
            {deps?.deletable ? (
              <Button data-testid="delete-training-confirm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">Elimina definitivamente</Button>
            ) : training.status !== "annullata" && (
              <Button data-testid="cancel-training-btn" onClick={cancelTraining} variant="outline">Segna come annullata</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SessionDiary({ diary, training, onLinkLesson }) {
  if (!diary) return <div className="text-sm text-zinc-500 py-8 text-center" data-testid="diary-empty">Diario non disponibile.</div>;
  const r = diary.realized || {};
  const c = diary.comparison || {};
  const t = diary.training || {};
  const catLabel = (k) => ({ attivazione: "Riscaldamento", mobilita: "Mobilità", prep_fisica: "Preparazione fisica", gambe_scherma: "Gambe scherma", tecnica: "Tecnica", tattica: "Tattica", coppie: "Tecnica", assalti_tema: "Assalti", assalti_liberi: "Assalti", circuito: "Preparazione fisica", test: "Test", simulazione: "Assalti", recupero: "Recupero" }[k] || k || "Attività");
  return (
    <div className="space-y-6" data-testid="session-diary">
      {/* Confronto sintetico durata */}
      <div className="flex items-center gap-2 text-lg" data-testid="diary-headline">
        <span className="font-mono font-bold">{c.planned_minutes ?? training?.duration ?? 0}′</span>
        <span className="text-zinc-400 text-sm">previsti →</span>
        <span className="font-mono font-bold text-[#0052FF]">{c.effective_minutes != null ? `${c.effective_minutes}′` : "—"}</span>
        <span className="text-zinc-400 text-sm">realizzati</span>
        {t.status === "completed" && <span className="ml-2 text-[11px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">Seduta chiusa</span>}
      </div>
      {/* Programmato vs Realizzato */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <DBox label="Durata prevista" v={`${c.planned_minutes ?? training?.duration ?? 0}′`} />
        <DBox label="Durata effettiva" v={c.effective_minutes != null ? `${c.effective_minutes}′` : "—"} testid="diary-effective" />
        <DBox label="Attesi vs presenti" v={`${c.present_athletes ?? 0}/${c.expected_athletes ?? 0}`} testid="diary-present" />
        <DBox label="Blocchi vs attività" v={(c.planned_blocks || c.registered_activities) ? `${c.planned_blocks ?? 0} → ${c.registered_activities ?? 0}` : "n/d"} />
      </div>

      {/* Confronto per categoria */}
      {(c.by_category || []).length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="diary-by-category">
          <h3 className="font-heading text-lg font-semibold mb-3">Per categoria</h3>
          <div className="space-y-1.5">
            {c.by_category.map((row) => (
              <div key={row.category} data-testid={`cat-row-${row.category}`} className="flex items-center justify-between text-sm">
                <span className="text-zinc-700">{row.category}</span>
                <span className="font-mono text-zinc-500">{row.planned}′ <span className="text-zinc-300">→</span> <span className="text-[#0052FF] font-semibold">{row.realized}′</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* PROGRAMMATO */}
        <div className="bg-white border border-zinc-200 rounded-md p-5">
          <h3 className="font-heading text-lg font-semibold mb-3">Programmato</h3>
          <div className="text-sm text-zinc-600 space-y-1">
            <div><span className="text-zinc-400">Gruppi:</span> {(t.group_names || []).join(", ") || "—"}</div>
            <div><span className="text-zinc-400">Intensità:</span> {t.intensity || "—"}</div>
            <div><span className="text-zinc-400">Obiettivo:</span> {t.objective || "—"}</div>
          </div>
          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Blocchi previsti ({(t.blocks || []).length})</div>
            {(t.blocks || []).length === 0 ? <div className="text-sm text-zinc-400">Nessun blocco.</div> : (
              <ul className="space-y-1">
                {t.blocks.map((b, i) => <li key={i} className="text-sm flex justify-between"><span>{b.title}</span><span className="font-mono text-zinc-500">{b.duration || 0}′</span></li>)}
              </ul>
            )}
          </div>
        </div>

        {/* REALIZZATO */}
        <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="diary-realized">
          <h3 className="font-heading text-lg font-semibold mb-3">Realizzato</h3>
          {!r.has_register && <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">Nessun registro presenze collegato a questa seduta.</div>}
          <div className="text-sm space-y-2">
            <div>
              <span className="text-zinc-400 text-[11px] uppercase tracking-wider">Tecnici presenti</span>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="diary-coaches">
                {(r.coaches || []).length === 0 ? <span className="text-zinc-400 text-sm">—</span> :
                  r.coaches.map((co) => <span key={co.id} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700">{co.name}</span>)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-zinc-400 text-[11px] uppercase tracking-wider">Presenti ({(r.present || []).length})</span>
                <ul className="mt-1 max-h-32 overflow-y-auto text-emerald-800">{(r.present || []).map((p) => <li key={p.athlete_id} className="text-xs">{p.name}</li>)}{(r.present || []).length === 0 && <li className="text-xs text-zinc-400">—</li>}</ul></div>
              <div><span className="text-zinc-400 text-[11px] uppercase tracking-wider">Assenti ({(r.absent || []).length})</span>
                <ul className="mt-1 max-h-32 overflow-y-auto text-red-800">{(r.absent || []).map((p) => <li key={p.athlete_id} className="text-xs">{p.name}</li>)}{(r.absent || []).length === 0 && <li className="text-xs text-zinc-400">—</li>}</ul></div>
            </div>
          </div>
        </div>
      </div>

      {/* Cosa abbiamo fatto */}
      <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="diary-activities">
        <h3 className="font-heading text-lg font-semibold mb-3 flex items-center justify-between">Cosa abbiamo fatto
          <Link to={`/scherma?tab=lesson&training_id=${training.id}`} data-testid="diary-add-lesson" className="text-xs font-normal uppercase tracking-wider text-[#0052FF] hover:underline">+ Lezione in questa seduta</Link>
        </h3>
        {(r.fencing_activities || []).length === 0 && (r.lessons || []).length === 0 ? (
          <div className="text-sm text-zinc-400">Nessuna attività o lezione registrata per questa seduta.</div>
        ) : (
          <div className="space-y-4">
            {(r.fencing_activities || []).map((fa) => (
              <div key={fa.id} className="border border-zinc-100 rounded p-3">
                <div className="text-xs text-zinc-500 mb-1">{fa.objective || "Attività di gruppo"} · {fa.duration || 0}′ · {fa.present_count || 0} presenti</div>
                <div className="flex flex-wrap gap-1.5">
                  {(fa.blocks || []).map((b, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-zinc-100 text-zinc-700">{catLabel(b.category)} {b.duration ? `· ${b.duration}′` : ""}</span>)}
                </div>
              </div>
            ))}
            {(r.lessons || []).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Lezioni individuali ({r.lessons.length})</div>
                <ul className="space-y-1" data-testid="diary-lessons">
                  {r.lessons.map((l) => <li key={l.id} data-testid={`diary-lesson-${l.id}`} className="text-sm flex justify-between"><span>{l.athlete_name || "—"} {l.objective ? `· ${l.objective}` : ""}</span><span className="font-mono text-zinc-500">{l.duration || 0}′</span></li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        {(r.unlinked_lessons || []).length > 0 && (
          <div className="mt-4 border-t border-dashed border-zinc-200 pt-3" data-testid="diary-unlinked-lessons">
            <div className="text-[11px] uppercase tracking-wider text-amber-700 mb-1">Lezioni dello stesso giorno non collegate a nessuna seduta ({r.unlinked_lessons.length})</div>
            <p className="text-xs text-zinc-500 mb-2">Registrate senza indicare la seduta: non vengono attribuite automaticamente. Collegale solo se svolte in questa seduta.</p>
            <ul className="space-y-1">
              {r.unlinked_lessons.map((l) => (
                <li key={l.id} data-testid={`unlinked-lesson-${l.id}`} className="text-sm flex items-center justify-between gap-2">
                  <span className="truncate">{l.athlete_name || "—"} {l.objective ? `· ${l.objective}` : ""} <span className="font-mono text-zinc-400">{l.duration || 0}′</span></span>
                  {onLinkLesson && <button data-testid={`link-lesson-${l.id}`} onClick={() => onLinkLesson(l.id)} className="text-xs px-2 py-1 rounded border border-zinc-300 hover:bg-zinc-50 shrink-0">Collega a questa seduta</button>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {(r.notes || []).length > 0 && (
          <div className="mt-4 border-t border-zinc-100 pt-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Note tecniche</div>
            {r.notes.map((n, i) => <p key={i} className="text-sm text-zinc-700">{n}</p>)}
          </div>
        )}
      </div>
    </div>
  );
}

function DBox({ label, v, testid }) {
  return <div className="bg-white border border-zinc-200 rounded-md p-4" data-testid={testid}><div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div><div className="font-heading text-2xl font-bold mt-1">{v}</div></div>;
}

function CoachChips({ coaches, selected, onToggle, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {coaches.length === 0 && <span className="text-xs text-zinc-400">—</span>}
      {coaches.map((u) => (
        <button key={u.id} type="button" data-testid={`${testidPrefix}-coach-${u.id}`} onClick={() => onToggle(u.id)}
          className={`text-xs px-2 py-1 rounded-full border ${selected.includes(u.id) ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300"}`}>{u.name}</button>
      ))}
    </div>
  );
}

function AssignSuggest({ training, category, blockRef, selected, onToggle }) {
  const [sugs, setSugs] = useState(null);
  const [loading, setLoading] = useState(false);
  const gid = (training.group_ids || [])[0];
  const fetchSug = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/technical/suggest", { kind: "activity", date: (training.date || "").slice(0, 10), group_id: gid, category });
      setSugs(data.suggestions || []);
    } catch { setSugs([]); } finally { setLoading(false); }
  };
  const accept = (s, proposed) => {
    onToggle(s.coach_id);
    api.post("/technical/assignment-event", { training_id: training.id, context: "activity", block_ref: blockRef, category,
      proposed_coach_ids: proposed, chosen_coach_ids: selected.includes(s.coach_id) ? selected.filter((x) => x !== s.coach_id) : [...selected, s.coach_id] }).catch(() => {});
  };
  if (sugs === null) {
    return <button type="button" data-testid={`suggest-btn-${blockRef}`} onClick={fetchSug} className="mt-1 text-[11px] text-[#0052FF] flex items-center gap-1"><Sparkles className="w-3 h-3" /> Suggerisci tecnici</button>;
  }
  const proposedIds = sugs.map((s) => s.coach_id);
  return (
    <div className="mt-1.5" data-testid={`suggest-list-${blockRef}`}>
      {loading && <span className="text-xs text-zinc-400">…</span>}
      {!loading && sugs.length === 0 && <span className="text-xs text-zinc-400">Nessun tecnico compatibile suggerito.</span>}
      <div className="flex flex-wrap gap-1.5">
        {sugs.map((s) => (
          <button key={s.coach_id} type="button" title={s.reason} data-testid={`suggest-accept-${blockRef}-${s.coach_id}`} onClick={() => accept(s, proposedIds)}
            className={`text-[11px] px-2 py-1 rounded-full border flex items-center gap-1 ${selected.includes(s.coach_id) ? "bg-emerald-600 text-white border-emerald-600" : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"}`}>
            {s.primary && <span>★</span>}{s.name}
          </button>
        ))}
      </div>
      {sugs[0] && (
        <div className="mt-1 space-y-0.5" data-testid={`suggest-reason-${blockRef}`}>
          {sugs.map((s) => <div key={s.coach_id} className="text-[10px] text-zinc-400">{s.primary ? "★ " : ""}{s.name}: {s.reason}</div>)}
        </div>
      )}
    </div>
  );
}

const RSTATUS = [
  { k: "svolto", l: "Svolto", Icon: Check, on: "bg-emerald-600 text-white border-emerald-600" },
  { k: "parziale", l: "Parziale", Icon: Minus, on: "bg-amber-500 text-white border-amber-500" },
  { k: "non_svolto", l: "Non svolto", Icon: X, on: "bg-red-600 text-white border-red-600" },
];

function CloseSessionDialog({ training, diary, coaches = [], categories = CATEGORIES, onClose, onSaved }) {
  const r = diary?.realized || {};
  const c = diary?.comparison || {};
  const existing = (r.blocks && r.blocks.length) ? r.blocks : null;
  const presentCoachIds = (r.coaches || []).map((x) => x.id);
  const initBlocks = (training.blocks || []).map((b, i) => ({
    ref: String(i), title: b.title, category: blockCategory(b),
    planned_minutes: b.duration || 0, effective_minutes: b.duration || 0, status: "svolto", is_extra: false,
    coach_ids: (b.coach_ids && b.coach_ids.length) ? b.coach_ids : presentCoachIds,
  }));
  const [blocks, setBlocks] = useState(existing ? existing.filter((b) => !b.is_extra) : initBlocks);
  const [extras, setExtras] = useState(existing ? existing.filter((b) => b.is_extra) : []);
  const proposedRef = useRef(Object.fromEntries((existing ? existing.filter((b) => !b.is_extra) : initBlocks).map((b) => [b.ref, b.coach_ids || []])));
  const [coachIds, setCoachIds] = useState((r.coaches || []).map((x) => x.id));
  const [eff, setEff] = useState(c.effective_minutes ?? training.duration ?? 90);
  const [notes, setNotes] = useState((r.notes || [])[0] || "");
  const staff = coaches;
  const [saving, setSaving] = useState(false);

  const presentCount = (r.present || []).length;
  const absentCount = (r.absent || []).length;
  const setBlk = (i, patch) => setBlocks((bs) => bs.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const setExtra = (i, patch) => setExtras((es) => es.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const addExtra = () => setExtras((es) => [...es, { ref: null, title: "", category: "Altro", planned_minutes: 0, effective_minutes: 15, status: "svolto", is_extra: true }]);
  const removeExtra = (i) => setExtras((es) => es.filter((_, idx) => idx !== i));
  const toggleCoach = (id) => setCoachIds((cs) => (cs.includes(id) ? cs.filter((x) => x !== id) : [...cs, id]));

  const warnings = [];
  if (coachIds.length === 0) warnings.push("tecnici presenti");
  if (!r.has_register && presentCount + absentCount === 0) warnings.push("presenze");

  const save = async () => {
    setSaving(true);
    try {
      const realized_blocks = [...blocks, ...extras].map((b) => ({
        ref: b.ref ?? null, title: b.title || b.category, category: b.category,
        planned_minutes: b.planned_minutes ?? null,
        effective_minutes: b.status === "non_svolto" ? 0 : (parseInt(b.effective_minutes) || 0),
        status: b.status, is_extra: !!b.is_extra, coach_ids: b.coach_ids || [],
      }));
      await api.post(`/training/${training.id}/close-session`, {
        effective_minutes: parseInt(eff) || null, coach_ids: coachIds, realized_blocks, notes: notes || null,
      });
      // Smart Close · logga override Proposto→Scelto (senza motivazioni)
      for (const b of blocks) {
        const proposed = proposedRef.current[b.ref] || [];
        const chosen = b.coach_ids || [];
        if (JSON.stringify([...proposed].sort()) !== JSON.stringify([...chosen].sort())) {
          api.post("/technical/assignment-event", { training_id: training.id, context: "activity", block_ref: b.ref, category: b.category, proposed_coach_ids: proposed, chosen_coach_ids: chosen }).catch(() => {});
        }
      }
      toast.success("Seduta chiusa"); onSaved();
    } catch { toast.error("Errore chiusura seduta"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="close-session-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="w-4 h-4" /> Chiudi seduta</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Riepilogo rapido */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-zinc-50 rounded-md p-2"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Presenti</div><div className="font-heading text-xl font-bold" data-testid="cs-present">{presentCount}<span className="text-sm text-zinc-400">/{presentCount + absentCount}</span></div></div>
            <div className="bg-zinc-50 rounded-md p-2"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Tecnici</div><div className="font-heading text-xl font-bold" data-testid="cs-tech-count">{coachIds.length}</div></div>
            <div className="bg-zinc-50 rounded-md p-2">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Durata eff.</div>
              <div className="flex items-center justify-center gap-1"><Input data-testid="cs-effective" type="number" className="w-16 h-8 text-center font-mono" value={eff} onChange={(e) => setEff(e.target.value)} /><span className="text-xs text-zinc-500">′</span></div>
            </div>
          </div>

          {/* Tecnici presenti */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Tecnici presenti</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="cs-coaches">
              {staff.length === 0 && <span className="text-xs text-zinc-400">Nessun tecnico disponibile</span>}
              {staff.map((u) => (
                <button key={u.id} type="button" data-testid={`cs-coach-${u.id}`} onClick={() => toggleCoach(u.id)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${coachIds.includes(u.id) ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300"}`}>{u.name}</button>
              ))}
            </div>
          </div>

          {/* Programmato → Svolto */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Programmato → Svolto</Label>
            {blocks.length === 0 && <div className="text-xs text-zinc-400 mt-1">Nessun blocco programmato.</div>}
            <div className="space-y-2 mt-1">
              {blocks.map((b, i) => (
                <div key={i} data-testid={`cs-block-${i}`} className="border border-zinc-200 rounded-md p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{b.title}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-400">{b.category} · prog. {b.planned_minutes}′</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Input type="number" className="w-14 h-8 text-center font-mono" data-testid={`cs-block-min-${i}`} value={b.effective_minutes} onChange={(e) => setBlk(i, { effective_minutes: e.target.value })} disabled={b.status === "non_svolto"} />
                      <span className="text-xs text-zinc-400">′</span>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-2">
                    {RSTATUS.map((s) => (
                      <button key={s.k} type="button" data-testid={`cs-block-${i}-${s.k}`} onClick={() => setBlk(i, { status: s.k })}
                        className={`flex-1 flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-md border ${b.status === s.k ? s.on : "bg-white text-zinc-500 border-zinc-200"}`}>
                        <s.Icon className="w-3.5 h-3.5" />{s.l}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400">Tecnici (realizzato)</span>
                    <CoachChips coaches={staff} selected={b.coach_ids || []} testidPrefix={`cs-block-${i}`}
                      onToggle={(cid) => setBlk(i, { coach_ids: (b.coach_ids || []).includes(cid) ? b.coach_ids.filter((x) => x !== cid) : [...(b.coach_ids || []), cid] })} />
                    <AssignSuggest training={training} category={b.category} blockRef={`cs-${i}`} selected={b.coach_ids || []}
                      onToggle={(cid) => setBlk(i, { coach_ids: (b.coach_ids || []).includes(cid) ? b.coach_ids.filter((x) => x !== cid) : [...(b.coach_ids || []), cid] })} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Attività extra */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Attività non previste</Label>
              <button type="button" data-testid="cs-add-extra" onClick={addExtra} className="text-xs text-[#0052FF] flex items-center gap-1"><Plus className="w-3 h-3" /> Aggiungi</button>
            </div>
            <div className="space-y-2 mt-1">
              {extras.map((e, i) => (
                <div key={i} data-testid={`cs-extra-${i}`} className="border border-zinc-200 rounded-md p-2.5 flex items-center gap-2">
                  <Input className="flex-1 h-8" placeholder="Titolo" data-testid={`cs-extra-title-${i}`} value={e.title} onChange={(ev) => setExtra(i, { title: ev.target.value })} />
                  <Select value={e.category} onValueChange={(v) => setExtra(i, { category: v })}>
                    <SelectTrigger data-testid={`cs-extra-cat-${i}`} className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-60">{categories.map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" className="w-14 h-8 text-center font-mono" data-testid={`cs-extra-min-${i}`} value={e.effective_minutes} onChange={(ev) => setExtra(i, { effective_minutes: ev.target.value })} />
                  <button type="button" onClick={() => removeExtra(i)} className="text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Note finali */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Note finali</Label>
            <Textarea data-testid="cs-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Osservazioni sulla seduta…" />
          </div>

          {/* Avviso discreto data quality */}
          {warnings.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="cs-warning">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Manca: {warnings.join(", ")}
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-200 rounded-md">Annulla</button>
          <button data-testid="cs-save" disabled={saving} onClick={save} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md">{saving ? "…" : "Chiudi seduta"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
